// Lib
import { FC, useEffect, useState } from "react";
import { Spin } from "antd";
// Hooks
import { useViewport } from "hooks";
// Types
import { FilterStateItem } from "types/tableFilters";
import { TableHeaderProps } from "../../types";
// Theme
import { theme } from "theme";
// Helpers
import {
  createFiltersDropdown,
  updateFiltersData,
} from "helpers/tableFiltersHelpers";
// Icons
import { SearchBlackIcon, TuneBlackFilterIcon } from "icons";
// Components
import { Badge, DropDown } from "components";
import { SearchInput } from "components/Form";
import { TableFiltersBar } from "components/TableFiltersBar";
// Styled
import { Typography } from "styled/Typography";
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";
import {
  HeaderTabs,
  MobileSearchWrapper,
  RedDot,
  Wrapper,
  MobileFilterButtonWrapper,
} from "./styled";

export const TableHeader: FC<TableHeaderProps> = ({
  isLoading,
  totalCount,
  ordersCalendarTable,
  search,
  filter,
  tabs,
  suffix,
  leftTitle,
}) => {
  const { isDesktop } = useViewport();

  const [collapsed, setCollapsed] = useState(true);

  const [isMobileSearch, setIsMobileSearch] = useState(false);

  const filtersData = filter?.include
    ? updateFiltersData(filter.filters, filter?.include)
    : filter?.filters;

  const dropdownFilters = !!filter && createFiltersDropdown(filtersData);

  const isAnyFilterExist = !!filter?.selected?.length;

  useEffect(() => {
    setCollapsed(!isAnyFilterExist);
  }, [isAnyFilterExist]);

  const setFilter = filterData => {
    if (
      !filter?.selected?.find(
        (f: FilterStateItem) => f?.name === filterData.key,
      )
    ) {
      const data: FilterStateItem[] = [
        ...filter.selected,
        {
          name: filterData.key,
          value: "",
          field: filtersData.find(filter => filter.key === filterData.key)
            .field,
        },
      ];

      filter?.setValue(data);
    }
  };

  const handleCollapseFilters = () => {
    setCollapsed(prev => !prev);
  };

  const headerItemsCount = [!!search].filter(Boolean)?.length;

  return (
    <Wrapper
      $column
      $ordersCalendarTable={ordersCalendarTable}
      $isSelectedFilter={!!filter?.selected}
    >
      <FlexContainer
        style={{ minHeight: 40 }}
        $align="center"
        $justify="space-between"
      >
        {leftTitle && <Typography.H2>{leftTitle}</Typography.H2>}

        {!!tabs && (
          <HeaderTabs
            $headerItemsCount={headerItemsCount}
            activeKey={tabs.tab && tabs.tab}
            items={tabs?.items}
            onTabClick={e => tabs.setTab(e)}
          />
        )}

        {totalCount ? (
          <Typography.Description>
            Total records
            <Badge
              title={`${totalCount}`}
              bgColor="white"
              textColor={theme.color.black}
              border={`1px solid ${theme.color.borderLight}`}
              style={{ marginLeft: 8 }}
            />
          </Typography.Description>
        ) : (
          <div />
        )}

        <FlexContainer $gap={10}>
          {!!search && isDesktop && (
            <SearchInput
              value={search.value}
              setValue={search.setValue}
              placeholder={search.placeholder}
            />
          )}

          {!!search && !isDesktop && (
            <Button.SquaredIcon
              icon={<SearchBlackIcon />}
              onClick={() => setIsMobileSearch(prev => !prev)}
            />
          )}

          {!!filter?.filters.length && isAnyFilterExist ? (
            <>
              {!isDesktop ? (
                <MobileFilterButtonWrapper>
                  <Button.SquaredIcon
                    icon={<TuneBlackFilterIcon />}
                    onClick={handleCollapseFilters}
                  />

                  <RedDot onClick={handleCollapseFilters} />
                </MobileFilterButtonWrapper>
              ) : (
                <Button.SubHeading
                  icon={<TuneBlackFilterIcon />}
                  onClick={handleCollapseFilters}
                >
                  Filter
                  <RedDot />
                </Button.SubHeading>
              )}
            </>
          ) : (
            !!filter?.filters.length && (
              <>
                {!isDesktop ? (
                  <DropDown
                    items={dropdownFilters}
                    trigger={["click"]}
                    onClick={setFilter}
                  >
                    <MobileFilterButtonWrapper>
                      <Button.SquaredIcon icon={<TuneBlackFilterIcon />} />
                      {isAnyFilterExist && <RedDot />}
                    </MobileFilterButtonWrapper>
                  </DropDown>
                ) : (
                  <DropDown
                    items={dropdownFilters}
                    trigger={["click"]}
                    onClick={setFilter}
                  >
                    <Button.SubHeading icon={<TuneBlackFilterIcon />}>
                      Filter
                      {isAnyFilterExist && <RedDot />}
                    </Button.SubHeading>
                  </DropDown>
                )}
              </>
            )
          )}
          {!!suffix && suffix}
        </FlexContainer>
      </FlexContainer>

      {!!search && !isDesktop && (
        <MobileSearchWrapper
          $isTabs={!!tabs}
          style={{
            height: isMobileSearch ? (collapsed ? "60px" : "50px") : "0px",
          }}
        >
          <SearchInput
            isMobile
            value={search.value}
            setValue={search.setValue}
            placeholder={search.placeholder}
          />
        </MobileSearchWrapper>
      )}

      {!!filter && (
        <Spin indicator={<></>} spinning={isLoading}>
          <TableFiltersBar
            collapsed={collapsed}
            value={filter?.value}
            selected={filter.selected}
            filters={filtersData}
            setValue={filter.setValue}
          />
        </Spin>
      )}
    </Wrapper>
  );
};
